import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { articles } from "../journal";
import { EASE, useLenis } from "../components/site/primitives";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";

const formatDate = (isoDate, displayFallback) => {
  if (displayFallback) return displayFallback;
  try {
    return new Date(isoDate).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
    });
  } catch {
    return isoDate;
  }
};

export default function Journal() {
  useLenis();
  return (
    <>
      <Helmet>
        <title>Journal | Hitakari House</title>
        <meta
          name="description"
          content="Essays and notes from Hitakari House. On hospitality, time, and the small, deliberate work of caring for a very small circle of people."
        />
        <link rel="canonical" href="https://www.hitakari.com/journal" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.hitakari.com/journal" />
        <meta property="og:title" content="Journal | Hitakari House" />
        <meta
          property="og:description"
          content="Essays and notes from Hitakari House. On hospitality, time, and the small, deliberate work of caring for a very small circle of people."
        />
        <meta property="og:image" content="https://www.hitakari.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Journal | Hitakari House" />
        <meta name="twitter:description" content="Essays and notes from Hitakari House." />
      </Helmet>

      <div style={{ background: "var(--ivory)", color: "var(--forest)", minHeight: "100vh" }} data-testid="journal-index-page">
        <Nav />

        <main className="pt-40 pb-32 px-6 md:px-10">
          <div className="mx-auto max-w-[900px]">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <span className="eyebrow" style={{ color: "var(--brass)" }}>The Journal</span>
              <h1 className="font-heading font-light leading-[1.05] tracking-[-0.01em] mt-6 text-[2.4rem] sm:text-[3.4rem] lg:text-[4rem]">
                Notes from the house.
              </h1>
              <p className="font-body italic text-xl md:text-2xl mt-6 max-w-xl leading-relaxed" style={{ color: "var(--olive)" }}>
                On hospitality, on time, and on the small things that make a life feel quietly attended to.
              </p>
            </motion.div>

            <div className="mt-20 md:mt-24" style={{ borderTop: "1px solid rgba(23,64,42,0.18)" }}>
              {articles.map((a, i) => (
                <motion.article
                  key={a.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10% 0px" }}
                  transition={{ duration: 0.8, delay: i * 0.06, ease: EASE }}
                  className="py-10 md:py-14"
                  style={{ borderBottom: "1px solid rgba(23,64,42,0.18)" }}
                  data-testid={`journal-item-${a.slug}`}
                >
                  <Link to={`/journal/${a.slug}`} className="group block">
                    <p className="font-label uppercase text-xs tracking-[0.24em] font-light" style={{ color: "var(--brass)" }}>
                      {formatDate(a.date, a.dateDisplay)}
                    </p>
                    <h2 className="font-heading font-light leading-[1.1] tracking-[-0.01em] mt-4 text-[1.8rem] sm:text-[2.4rem] lg:text-[2.8rem] link-underline-slow">
                      {a.title}
                    </h2>
                    <p className="font-body italic text-xl md:text-2xl mt-4 max-w-2xl leading-snug" style={{ color: "var(--olive)" }}>
                      {a.standfirst}
                    </p>
                    <p className="mt-5 font-label uppercase text-[0.7rem] tracking-[0.28em] font-light" style={{ color: "var(--forest)", opacity: 0.65 }}>
                      Read essay &nbsp;&rarr;
                    </p>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
